/**
 * O acesso pelo QR a uma versão emitida (E145.F2, GUS-181, ADR-0099 §2.6). Espelha
 * `apps/api/types/acesso_por_qr.py`.
 *
 * - Equipe: `GET|POST /casos/:id/emissoes/:emissao/acessos` e
 *   `POST /casos/:id/acessos/:acesso/revogacao`. A chave sai só na emissão.
 * - Público, sem sessão: `POST /acesso/versao` e `POST /acesso/saida`. A chave vai no corpo; na
 *   página ela vem do fragmento do endereço (`/acesso#…`), que o navegador não manda ao servidor.
 */
import axios, { type AxiosInstance } from 'axios';

import { codigoDeVerificacao, nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';

export type TipoDeSaida = 'laudo' | 'resumo' | 'evidencias' | 'riscos';

export interface AcessoPorQrData {
  id: string;
  case_id: string;
  emissao_id: string;
  criado_por: string | null;
  criado_em: string;
  revogado_em: string | null;
  revogado_por: string | null;
}

export interface AcessoEmitidoData {
  acesso: AcessoPorQrData;
  chave: string;
  caminho: string;
}

export interface VersaoLiberadaData {
  versao: number;
  emitido_em: string;
  codigo: string;
  saidas: { tipo: TipoDeSaida }[];
}

/**
 * `indisponivel`: a chave não abre nada (a recusa não diz por quê). `rede`: tente de novo.
 * `muitas_tentativas`: o limite de tentativas da api (E166.F2.T02); espere antes de tentar.
 */
export type MotivoDaRecusaDoAcesso = 'indisponivel' | 'rede' | 'muitas_tentativas';

export class AcessoPorQrError extends Error {
  constructor(readonly motivo: MotivoDaRecusaDoAcesso) {
    super(motivo);
    this.name = 'AcessoPorQrError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;
const HTTP_TOO_MANY_REQUESTS = 429;

function motivo(err: unknown): MotivoDaRecusaDoAcesso {
  if (!axios.isAxiosError(err)) return 'rede';
  const status = err.response?.status;
  if (status === HTTP_TOO_MANY_REQUESTS) return 'muitas_tentativas';
  return status === HTTP_NOT_FOUND || status === HTTP_UNPROCESSABLE ? 'indisponivel' : 'rede';
}

/** A chave do fragmento do endereço, sem o `#`; vazia quando não há. */
export function chaveDoEndereco(hash: string): string {
  return decodeURIComponent(hash.replace(/^#/, '')).trim();
}

/** O link completo a entregar: a origem desta instalação mais o caminho devolvido na emissão. */
export function linkDoAcesso(caminho: string, origem: string = window.location.origin): string {
  return `${origem}${caminho}`;
}

export async function abrirVersao(
  client: AxiosInstance,
  chave: string,
): Promise<VersaoLiberadaData> {
  try {
    const { data } = await client.post<VersaoLiberadaData>('/acesso/versao', { chave });
    return data;
  } catch (err) {
    throw new AcessoPorQrError(motivo(err));
  }
}

export async function baixarSaida(
  client: AxiosInstance,
  chave: string,
  tipo: TipoDeSaida,
): Promise<ArquivoBaixado> {
  try {
    const resp = await client.post<Blob>(
      '/acesso/saida',
      { chave, tipo },
      { responseType: 'blob' },
    );
    return {
      blob: resp.data,
      filename: nomeDoAnexo(resp.headers['content-disposition'], `${tipo}.pdf`),
      codigoVerificacao: codigoDeVerificacao(resp.headers),
    };
  } catch (err) {
    throw new AcessoPorQrError(motivo(err));
  }
}

export async function listarAcessos(
  client: AxiosInstance,
  caseId: string,
  emissaoId: string,
): Promise<AcessoPorQrData[]> {
  const { data } = await client.get<AcessoPorQrData[]>(
    `/casos/${caseId}/emissoes/${emissaoId}/acessos`,
  );
  return data;
}

export async function emitirAcesso(
  client: AxiosInstance,
  caseId: string,
  emissaoId: string,
): Promise<AcessoEmitidoData> {
  const { data } = await client.post<AcessoEmitidoData>(
    `/casos/${caseId}/emissoes/${emissaoId}/acessos`,
  );
  return data;
}

export async function revogarAcesso(
  client: AxiosInstance,
  caseId: string,
  acessoId: string,
): Promise<AcessoPorQrData> {
  const { data } = await client.post<AcessoPorQrData>(
    `/casos/${caseId}/acessos/${acessoId}/revogacao`,
  );
  return data;
}
